import { ReactNode, useCallback } from 'react';

import { useToast as useChakraToast, ToastId } from '@chakra-ui/react';

export interface IToastData {
  title: string;
  description?: ReactNode;
  variant?: 'warning' | 'error' | 'success' | 'info';
  duration?: number;
  onCloseComplete?: () => void;
}

interface IUseToastResponse {
  show(data: IToastData): ToastId | undefined;
  update(id: ToastId, data: IToastData): void;
  closeAll(): void;
}

export const useToast = (): IUseToastResponse => {
  const chakraToast = useChakraToast();

  const show = useCallback(
    (data: IToastData) => {
      const id = `${data.title}${
        typeof data.description === 'string' ? `-${data.description}` : ''
      }`;

      const isToastActive = chakraToast.isActive(id);

      if (isToastActive) return;

      return chakraToast({
        isClosable: true,
        position: 'top-right',
        id,
        title: data.title,
        status: data.variant,
        description: data.description,
        duration: data.duration || 4000,
        onCloseComplete: data.onCloseComplete,
      });
    },
    [chakraToast]
  );

  const update = useCallback(
    (id: ToastId, data: IToastData) => {
      if (!chakraToast.isActive(id)) return;

      chakraToast.update(id, {
        title: data.title,
        status: data.variant,
        description: data.description,
        duration: data.duration || 4000,
        onCloseComplete: data.onCloseComplete,
      });
    },
    [chakraToast]
  );

  const closeAll = useCallback(() => {
    chakraToast.closeAll();
  }, [chakraToast]);

  return { show, update, closeAll };
};
